/**
 * usePeerConnection — Subscribe to a single peer's state and controls.
 *
 * Listens to EventBus events for a specific peerId and mirrors the
 * PeerConnection's current state (also when the component mounts after the
 * peer already connected). Provides connect/disconnect/mute/toggle controls per peer.
 */

import { useState, useEffect, useCallback } from "react";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, WEBRTC_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { PEER_STATES, PEER_INTERNAL_EVENTS } from "features/robot-teleop/webrtc/peerStates";

const readSnapshot = (peer) => ({
  state: peer?.state ?? PEER_STATES.IDLE,
  iceState: peer?.iceState ?? null,
  connectionState: peer?.connectionState ?? null,
  retryCount: peer?.retryCount ?? 0,
  microphoneEnabled: peer?.microphoneEnabled ?? false,
  remoteAudioEnabled: peer?.remoteAudioEnabled ?? true,
  userClosed: peer?.userClosed ?? false,
});

export const usePeerConnection = (peerId) => {
  const { manager, connectPeer, disconnectPeer, setAutoConnect } = useConnectionManager();
  const eventBus = useEventBus();

  // Get static info from PeerConnection instance
  const peer = peerId ? manager?.getPeer(peerId) : undefined;
  const cameras = peer?.cameras || [];
  const microphones = peer?.microphones || [];
  const speakers = peer?.speakers || [];
  const isControlChannel = peer?.isControlChannel || false;
  const autoConnect = peer?.autoConnect || false;
  const hasCamera = peer?.hasCamera || false;
  const hasMicrophone = peer?.hasMicrophone || false;
  const hasSpeaker = peer?.hasSpeaker || false;

  const [snapshot, setSnapshot] = useState(() => readSnapshot(peer));

  // Subscribe to state changes (and sync the current state on (re)subscribe)
  useEffect(() => {
    const current = peerId ? manager?.getPeer(peerId) : undefined;
    setSnapshot(readSnapshot(current));
    if (!peerId) return undefined;

    const refresh = () => setSnapshot(readSnapshot(manager?.getPeer(peerId)));
    // Machine transitions (idle/connecting/connected/...) ...
    const unsubState = eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE), refresh);
    // ... and raw RTCPeerConnection transport changes (e.g. 'disconnected' while still CONNECTED).
    const unsubTransport = eventBus.subscribe(getRobotEvent(peerId, WEBRTC_EVENTS.CONNECTION_STATE_CHANGED), refresh);
    // ... and the per-peer audio settings (microphone / robot audio).
    const unsubMedia = eventBus.subscribe(getRobotEvent(peerId, PEER_INTERNAL_EVENTS.MEDIA_CONFIG_CHANGED), refresh);

    return () => {
      if (typeof unsubState === 'function') unsubState();
      if (typeof unsubTransport === 'function') unsubTransport();
      if (typeof unsubMedia === 'function') unsubMedia();
    };
  }, [peerId, manager, eventBus]);

  const { state, iceState, connectionState, retryCount, microphoneEnabled, remoteAudioEnabled, userClosed } = snapshot;

  // Actions
  const connect = useCallback(() => connectPeer(peerId), [peerId, connectPeer]);
  const disconnect = useCallback(() => disconnectPeer(peerId), [peerId, disconnectPeer]);

  // User action: forced, so it also re-dials while an offer is still in flight.
  const reconnect = useCallback(() => {
    const p = manager?.getPeer(peerId);
    if (p) {
      const stream = manager?.sharedMedia?.getStream();
      p.reconnect(stream, { force: true });
    }
  }, [peerId, manager]);

  /** Send the browser microphone to this peer. Rejects when the microphone is denied. */
  const setMicrophoneEnabled = useCallback(
    (enabled) => Promise.resolve(manager?.setMicrophoneEnabled?.(peerId, enabled)),
    [peerId, manager]
  );

  /** Receive this peer's microphone (muted locally and on the robot when off). */
  const setRemoteAudioEnabled = useCallback((enabled) => {
    manager?.setRemoteAudioEnabled?.(peerId, enabled);
  }, [peerId, manager]);

  const muteLocalAudio = useCallback((muted) => {
    manager?.getPeer(peerId)?.muteLocalAudio(muted);
  }, [peerId, manager]);

  const muteLocalVideo = useCallback((muted) => {
    manager?.getPeer(peerId)?.muteLocalVideo(muted);
  }, [peerId, manager]);

  const sendMediaConfig = useCallback(async (config) => {
    return manager?.getPeer(peerId)?.sendMediaConfig(config);
  }, [peerId, manager]);

  const toggleAutoConnect = useCallback((enabled) => {
    setAutoConnect(peerId, enabled);
  }, [peerId, setAutoConnect]);

  /** @returns {boolean} true if the data was handed to the channel */
  const channelSend = useCallback((label, data) => {
    return !!manager?.getPeer(peerId)?.channelSend(label, data);
  }, [peerId, manager]);

  const enableStats = useCallback((intervalMs) => {
    manager?.getPeer(peerId)?.enableStats(intervalMs);
  }, [peerId, manager]);

  const disableStats = useCallback(() => {
    manager?.getPeer(peerId)?.disableStats();
  }, [peerId, manager]);

  return {
    // State
    state,
    iceState,
    connectionState,
    retryCount,
    isConnected: state === PEER_STATES.CONNECTED,
    isConnecting: state === PEER_STATES.CONNECTING || state === PEER_STATES.RECONNECTING,
    isFailed: state === PEER_STATES.FAILED,
    isIdle: state === PEER_STATES.IDLE,

    // Static info
    cameras,
    microphones,
    speakers,
    hasCamera,
    hasMicrophone,
    hasSpeaker,
    isControlChannel,
    autoConnect,

    // Per-peer audio settings
    microphoneEnabled,
    remoteAudioEnabled,
    userClosed,

    // Actions
    connect,
    disconnect,
    reconnect,
    setMicrophoneEnabled,
    setRemoteAudioEnabled,
    muteLocalAudio,
    muteLocalVideo,
    sendMediaConfig,
    setAutoConnect: toggleAutoConnect,
    channelSend,
    enableStats,
    disableStats,
  };
};
